import { apiRequest } from './api';

export interface GrupoUsuario {
  id: number;
  nome: string;
  slug: string;
  descricao?: string | null;
  is_system: boolean;
  createdAt: string;
  updatedAt: string;
  permissoes?: string[];
}

export interface PermissionCatalog {
  code: string;
  modulo: string;
  label: string;
}

export interface PermissionCatalogResponse {
  permissions: Record<string, PermissionCatalog>;
  modules: Record<string, Array<{ code: string; label: string }>>;
}

export const grupoUsuarioService = {
  // Listar todos os grupos
  list: async (): Promise<GrupoUsuario[]> => {
    try {
      const response = await apiRequest.get<GrupoUsuario[]>(
        '/grupos-usuarios'
      );
      return response.data || [];
    } catch (error: any) {
      console.error('❌ Erro ao listar grupos:', error.response?.status, error.response?.data || error.message);
      throw error;
    }
  },

  // Obter um grupo específico
  getById: async (id: number): Promise<GrupoUsuario> => {
    try {
      const response = await apiRequest.get<GrupoUsuario>(
        `/grupos-usuarios/${id}`
      );
      return response.data!;
    } catch (error: any) {
      console.error('❌ Erro ao buscar grupo:', error.response?.status, error.response?.data || error.message);
      throw error;
    }
  },

  // Obter catálogo de permissões
  getCatalog: async (): Promise<PermissionCatalogResponse> => {
    try {
      const response = await apiRequest.get<PermissionCatalogResponse>(
        '/grupos-usuarios/permissions/catalog'
      );
      return response.data || { permissions: {}, modules: {} };
    } catch (error: any) {
      console.error('❌ Erro ao carregar catálogo:', error.response?.status, error.response?.data || error.message);
      throw error;
    }
  },

  // Criar novo grupo
  create: async (data: {
    nome: string;
    slug?: string;
    descricao?: string;
    permissoes?: string[];
  }): Promise<GrupoUsuario> => {
    try {
      const response = await apiRequest.post<GrupoUsuario>(
        '/grupos-usuarios',
        data
      );
      return response.data!;
    } catch (error: any) {
      console.error('❌ Erro ao criar grupo:', error.response?.status, error.response?.data || error.message);
      throw error;
    }
  },

  // Atualizar grupo
  update: async (
    id: number,
    data: {
      nome?: string;
      slug?: string;
      descricao?: string;
      permissoes?: string[];
    }
  ): Promise<GrupoUsuario> => {
    try {
      const response = await apiRequest.put<GrupoUsuario>(
        `/grupos-usuarios/${id}`,
        data
      );
      return response.data!;
    } catch (error: any) {
      console.error('❌ Erro ao atualizar grupo:', error.response?.status, error.response?.data || error.message);
      throw error;
    }
  },

  // Atualizar permissões do grupo
  updatePermissoes: async (
    id: number,
    permissoes: string[]
  ): Promise<GrupoUsuario> => {
    try {
      const response = await apiRequest.put<GrupoUsuario>(
        `/grupos-usuarios/${id}/permissoes`,
        { permissoes }
      );
      return response.data!;
    } catch (error: any) {
      console.error('❌ Erro ao atualizar permissões:', error.response?.status, error.response?.data || error.message);
      throw error;
    }
  },

  // Duplicar grupo
  duplicate: async (
    id: number,
    data: {
      nome: string;
      slug?: string;
      descricao?: string;
    }
  ): Promise<GrupoUsuario> => {
    try {
      const response = await apiRequest.post<GrupoUsuario>(
        `/grupos-usuarios/${id}/duplicar`,
        data
      );
      return response.data!;
    } catch (error: any) {
      console.error('❌ Erro ao duplicar grupo:', error.response?.status, error.response?.data || error.message);
      throw error;
    }
  },

  // Deletar grupo
  delete: async (id: number): Promise<void> => {
    try {
      await apiRequest.delete(`/grupos-usuarios/${id}`);
    } catch (error: any) {
      console.error('❌ Erro ao deletar grupo:', error.response?.status, error.response?.data || error.message);
      throw error;
    }
  },
};

export default grupoUsuarioService;
